/**
 * Mounts the page for the current path. The font is imported here so every page shares one copy.
 *
 * The upload provider wraps every page, so files chosen on the landing page are still in memory
 * when the visitor arrives at /start. Choosing files anywhere but the flow moves into it. Only the
 * landing page, /start and /account/labs render `WindowDropTarget`, so a file dropped on a clinic,
 * account or checkout page is refused rather than starting a customer upload.
 */
import "@fontsource-variable/instrument-sans/standard.css";
import { lazy, StrictMode, Suspense } from "react";
import { createRoot } from "react-dom/client";
import { Account } from "./Account.tsx";
import { AccountNotFound } from "./account/NotFound.tsx";
import { AccountBilling } from "./account/Billing.tsx";
import { AccountGate } from "./account/AccountGate.tsx";
import { AccountClinics } from "./account/Clinics.tsx";
import { AccountFormula } from "./account/Formula.tsx";
import { AccountLabs } from "./account/Labs.tsx";
import { AccountOrders } from "./account/Orders.tsx";
import { AccountIntake } from "./account/Intake.tsx";
import { AccountPrivacy } from "./account/Privacy.tsx";
import { App } from "./App.tsx";
import { CheckIn } from "./CheckIn.tsx";
import { ClinicAccept } from "./clinic/Accept.tsx";
import { ClinicApp } from "./clinic/App.tsx";
import { ClinicLink } from "./clinic/Link.tsx";
import { ClinicNotFound } from "./clinic/NotFound.tsx";
import { isPatientsPath, PatientsApp } from "./clinic/PatientsApp.tsx";
import { ClinicSignIn } from "./clinic/SignIn.tsx";
import { Compare } from "./compare/Compare.tsx";
import { Claim } from "./Claim.tsx";
import { CheckoutIntake } from "./CheckoutIntake.tsx";
import { ConfirmSignIn } from "./ConfirmSignIn.tsx";
import { Privacy, Terms } from "./Legal.tsx";
import { NotFound } from "./NotFound.tsx";
import { Reaction } from "./Reaction.tsx";
import { Support } from "./Support.tsx";
import { Unsubscribe } from "./Unsubscribe.tsx";
import { navigate, usePath } from "./router.ts";
import { LabsStep } from "./start/LabsStep.tsx";
import { Questions } from "./start/Questions.tsx";
import { Reviewing } from "./start/Reviewing.tsx";
import { SecondFactorGate } from "./security/SecondFactorGate.tsx";
import { SignIn } from "./SignIn.tsx";
import { StaffInvitation } from "./StaffInvitation.tsx";
import { SecuritySetup } from "./security/Setup.tsx";
import { SecurityVerify } from "./security/Verify.tsx";
import { UploadProvider, WindowDropTarget } from "./Upload.tsx";

/* The account's labs page reads the same uploads to add reports to an existing account, so files
   chosen there stay there instead of restarting onboarding. */
function goToStart() {
  const path = window.location.pathname;

  if (!path.startsWith("/start") && path !== "/account/labs") navigate("/start");
}

/* The component gallery, in development only. In a production build the condition is false and the
   import, with the gallery in it, is dropped. */
const Gallery = import.meta.env.DEV
  ? lazy(async () => ({ default: (await import("./dev/Gallery.tsx")).Gallery }))
  : null;

const Overlays = import.meta.env.DEV
  ? lazy(async () => ({ default: (await import("./dev/Overlays.tsx")).Overlays }))
  : null;

const Layout = import.meta.env.DEV
  ? lazy(async () => ({ default: (await import("./dev/Layout.tsx")).Layout }))
  : null;

function AccountPage({ path }: { readonly path: string }) {
  switch (path) {
    case "/account/intake":
      return <AccountIntake />;
    case "/account/clinics":
      return <AccountClinics />;
    case "/account/privacy":
      return <AccountPrivacy />;
    case "/account/formula":
      return <AccountFormula />;
    case "/account/orders":
      return <AccountOrders />;
    case "/account/labs":
      return (
        <>
          <AccountLabs />
          <WindowDropTarget />
        </>
      );
    case "/account/billing":
      return <AccountBilling />;
    case "/account":
    case "/account/":
      return <Account />;
    default:
      return <AccountNotFound />;
  }
}

/** The development-only pages, which are absent from a production build. */
function developmentPage(path: string) {
  const Shown =
    { "/dev/ui": Gallery, "/dev/overlays": Overlays, "/dev/layout": Layout }[path] ?? null;

  return Shown === null ? null : (
    <Suspense fallback={null}>
      <Shown />
    </Suspense>
  );
}

function Page() {
  const path = usePath();

  const development = developmentPage(path);

  if (development !== null) return development;

  if (path.startsWith("/unsubscribe")) return <Unsubscribe />;

  if (path.startsWith("/check-in")) return <CheckIn />;

  if (path === "/practitioners") return <ClinicSignIn />;

  if (path === "/clinic/accept") return <ClinicAccept />;

  if (path === "/clinic/link") return <ClinicLink />;

  if (isPatientsPath(path))
    return (
      <SecondFactorGate>
        <PatientsApp />
      </SecondFactorGate>
    );

  if (path === "/clinic" || path === "/clinic/")
    return (
      <SecondFactorGate>
        <ClinicApp />
      </SecondFactorGate>
    );

  if (path.startsWith("/clinic/")) return <ClinicNotFound />;

  if (path.startsWith("/account"))
    return (
      <AccountGate>
        <AccountPage path={path} />
      </AccountGate>
    );

  if (path === "/compare" || path.startsWith("/compare/")) return <Compare />;

  if (path === "/claim") return <Claim />;

  switch (path) {
    case "/sign-in":
      return <SignIn />;
    case "/sign-in/confirm":
      return <ConfirmSignIn />;
    case "/invitations/staff":
      return <StaffInvitation />;
    case "/security/setup":
      return <SecuritySetup />;
    case "/security/verify":
      return <SecurityVerify />;
    case "/privacy":
      return <Privacy />;
    case "/terms":
      return <Terms />;
    case "/support/reaction":
      return <Reaction />;
    case "/support":
      return <Support />;
    case "/start/questions":
      return <Questions />;
    case "/start/reviewing":
      return <Reviewing />;
    case "/checkout":
      return (
        <AccountGate>
          <CheckoutIntake />
        </AccountGate>
      );
    case "/start":
      return (
        <>
          <LabsStep />
          <WindowDropTarget />
        </>
      );
    case "/":
      return (
        <>
          <App />
          <WindowDropTarget />
        </>
      );
    default:
      return <NotFound />;
  }
}

const root = document.getElementById("root");

if (!root) throw new Error("index.html has no #root to mount into");

createRoot(root).render(
  <StrictMode>
    <UploadProvider onAdded={goToStart}>
      <Page />
    </UploadProvider>
  </StrictMode>,
);
